import { GridItem, useToast } from '@chakra-ui/react';

import { isEmpty } from 'lodash';
import { useMutation } from 'urql';

import { useTranslation } from 'react-i18next';

import { useParams } from 'react-router-dom';
import { usePanelFullscreen } from 'src/components/common/PanelFullscreenContext';
import SparkelCopilot from 'src/components/common/SparkelCopilot';
import { useViewerMode, ViewerMode } from 'src/components/common/viewer-mode';
import ShapesToolbar from 'src/components/orderContractor/ShapesToolbar';
import SheetShapesToolbar from 'src/components/orderContractor/SheetShapesToolbar';
import {
  GeometricProperty,
  Point,
  SPARKEL_GEOMETRIC_PROPERTY_SET,
} from 'src/domain/geometry/geometric-types';
import { resolveElementIdentifiersDBID } from 'src/domain/property-operations';
import {
  CalculationTypeInput,
  CopilotResult,
  CreateOrderEntryDocument,
  CreateOrderEntryInput,
  ProtanProjectStepEnum,
  ProtanProjectStepStatusEnum,
  QuantityEnum,
  QuantityOperation,
  Unit,
} from 'src/gql/graphql';
import { useBimShapes } from 'src/hooks/bim-shapes';
import { usePropertyContext } from 'src/hooks/property-resolving';
import { useSheetShapes } from 'src/hooks/sheet-shapes';
import { useUserTenant } from 'src/services/auth-info';
import { hasDbIds } from 'src/services/viewer-services';
import { useVisibility } from 'src/services/viewer/visibility';
import { GRADIENTS } from 'src/utils/row-color-util';
import { convertGeometricPropertyToUnit } from 'src/utils/unit-utils';
import { useProtanProjectSteps } from 'src/components/common/ProtanProjectStepsProvider';
import ProtanSheetShapesToolbar from 'src/components/orderContractor/ProtanSheetShapesToolbar';
import ProtanSheetViewerV2ShapesToolbar from 'src/components/orderContractor/ProtanSheetViewerV2ShapesToolbar';
import { useSelectedOrder } from 'src/hooks/selected-order';
import SheetViewerV2ShapesToolbar from 'src/components/viewers/SheetsViewerV2/SheetViewerV2ShapesToolbar';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';

// eslint-disable-next-line complexity
export const CopilotToolbar = ({
  isContractor,
  projectId,
  projectSettings,
}: {
  isContractor: boolean;
  projectId: string;
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  };
}) => {
  const { isFullscreen } = usePanelFullscreen();
  const params = useParams();
  const { selectedOrder } = useSelectedOrder();
  const isAggregatedOrder = !!selectedOrder?.order?.isAggregatedOrder;
  const order = selectedOrder?.order;
  const orderEntryPanelOpen = !!params.orderId;
  const {
    drawing: { isDrawing: isDrawing3D },
  } = useBimShapes();

  const {
    drawing: { isDrawing: isDrawingSheetsV1 },
  } = useSheetShapes();
  const {
    drawing: { isDrawing: isDrawingSheetsV2 },
  } = useSheetViewerV2();
  const isDrawingSheets = isDrawingSheetsV1 || isDrawingSheetsV2;

  const { t } = useTranslation('project');

  const [, createOrderEntryMutation] = useMutation(CreateOrderEntryDocument);

  const createOrderEntry = async (orderEntryInput: CreateOrderEntryInput) => {
    const result = await createOrderEntryMutation({
      input: orderEntryInput,
    });
    if (result.error) {
      console.error(result.error);
      toast({
        status: 'error',
        title: t('toast.error.create-item'),
        position: 'top',
      });
    } else {
      const created = result.data?.createOrderEntry?.orderEntry;

      toast({
        status: 'success',
        position: 'top',
        title: t('toast.success.create-item'),
      });

      return created?.id;
    }
  };

  const { viewerMode } = useViewerMode();

  const { isolate } = useVisibility();

  const toast = useToast();

  const { propertyContext, isAllPropertiesLoaded } =
    usePropertyContext(projectId);

  const onCopilotResult = async (result: CopilotResult, request: string) => {
    if (result.__typename !== 'CopilotResultData') {
      return;
    }
    const dbIds = await resolveElementIdentifiersDBID(
      propertyContext,
      {
        predicates: result.attributeQuery.predicates,
      },
      projectSettings
    );

    const dbIdObject = Object.fromEntries(
      dbIds.map((dbId) => [dbId.modelUrn, dbId.dbIds])
    );

    let calculation: CalculationTypeInput;

    //This logic should be split into seperate functions to make clear what is what here.
    const filteredQuantityExtractions = result.quantity_extraction?.filter(
      (predicate) => {
        if (
          predicate.propertySet === SPARKEL_GEOMETRIC_PROPERTY_SET &&
          !Object.values(GeometricProperty).includes(
            predicate.propertyName as GeometricProperty
          )
        ) {
          console.log(
            'Invalid name for Sparkel property',
            predicate.propertyName
          );
          return false;
        } else {
          return true;
        }
      }
    );

    let unit = Unit.M2;
    if (filteredQuantityExtractions && filteredQuantityExtractions.length > 0) {
      const propertySet = filteredQuantityExtractions[0].propertySet;
      const propertyName = filteredQuantityExtractions[0].propertyName;
      const operation = filteredQuantityExtractions[0].operation;
      calculation = {
        attribute: {
          category: propertySet,
          name: propertyName,
        },
        operation: operation
          ? (operation as QuantityOperation)
          : QuantityOperation.Sum,
      };
      filteredQuantityExtractions.forEach((quantityExtraction) => {
        if (quantityExtraction.operation === QuantityOperation.Count) {
          unit = Unit.Pcs;
          return;
        }

        if (
          quantityExtraction.propertySet === SPARKEL_GEOMETRIC_PROPERTY_SET &&
          Object.values(GeometricProperty).includes(
            quantityExtraction.propertyName as GeometricProperty
          )
        ) {
          unit = convertGeometricPropertyToUnit(
            propertyName as GeometricProperty
          );
        }
      });
    } else {
      calculation = {
        attribute: {
          category: SPARKEL_GEOMETRIC_PROPERTY_SET,
          name: GeometricProperty.LARGEST_SIDE_AREA,
        },
        operation: QuantityOperation.Sum,
      };
    }

    //Handle breakdown by:
    const groupByAttributes =
      result.breakdown && result.breakdown.length > 0
        ? result.breakdown?.map((breakdown) => {
            return {
              category: breakdown.propertySet,
              name: breakdown.propertyName,
            };
          })
        : [{ category: 'Element', name: 'Name' }];

    if (hasDbIds(dbIdObject)) {
      isolate(dbIdObject);
      if (orderEntryPanelOpen && order) {
        const createOrderEntryInput: CreateOrderEntryInput = {
          orderEntry: {
            name: request,
            orderId: order.id,
            unit: unit,
            color: GRADIENTS[Math.floor(Math.random() * GRADIENTS.length)],
            quantity: {
              type: QuantityEnum.Dynamic,
              calculation: calculation,
              stringValue: null,
              value: null,
              derivedFormula: null,
            },
            groupByAttributes: groupByAttributes,
            // unitPrice: newFormValues.unitPrice,
            // currency: newFormValues.currency,
            elementIdentifiersQuery: {
              predicates: result.attributeQuery.predicates,
            },
          },
        };
        // Do not create order entry if we are not in the order entry route
        createOrderEntry(createOrderEntryInput);
      }
    } else {
      toast({
        title: t('toast.copilot.no-elements-found-title'),
        description: t('toast.copilot.no-elements-found-description'),
        status: 'warning',
      });
    }
  };

  const isDrawing = isDrawing3D || isDrawingSheets;

  const user = useUserTenant();

  const { projectSteps } = useProtanProjectSteps();

  const isRoofOutlineCompleted =
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.RoofOutline
    )?.status === ProtanProjectStepStatusEnum.Completed;

  const isDrainLineCompleted =
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.DrainLinePlacement
    )?.status === ProtanProjectStepStatusEnum.Completed;

  const isDrainPlacementCompleted =
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.DrainPlacement
    )?.status === ProtanProjectStepStatusEnum.Completed;

  const shouldUseProtanSheetShapesToolbar =
    user.isProtan &&
    viewerMode === ViewerMode.Sheets &&
    (!isRoofOutlineCompleted ||
      !isDrainLineCompleted ||
      !isDrainPlacementCompleted);

  // Helper function to determine which toolbar to render
  const renderToolbar = () => {
    if (!isContractor) {
      return null;
    }

    // Models viewer
    if (viewerMode === ViewerMode.Models) {
      return <ShapesToolbar />;
    }

    // Protan-specific toolbars
    if (shouldUseProtanSheetShapesToolbar && order) {
      if (viewerMode === ViewerMode.Sheets) {
        return <ProtanSheetShapesToolbar order={order} />;
      } else if (viewerMode === ViewerMode.SheetsV2) {
        return <ProtanSheetViewerV2ShapesToolbar order={order} />;
      }
    }

    // Default toolbars
    if (viewerMode === ViewerMode.Sheets) {
      return <SheetShapesToolbar />;
    }

    if (viewerMode === ViewerMode.SheetsV2) {
      return <SheetViewerV2ShapesToolbar />;
    }

    return null;
  };

  // Hide the toolbar when in fullscreen mode
  if (isFullscreen) {
    return null;
  }

  return (
    <GridItem area="toolbar" justifySelf="center" alignSelf="end" zIndex={9}>
      {renderToolbar()}
      {isContractor &&
        !isDrawing &&
        !isAggregatedOrder &&
        isAllPropertiesLoaded &&
        !isEmpty(propertyContext.loadedModels) && (
          <SparkelCopilot
            projectId={projectId}
            onCopilotResult={onCopilotResult}
          />
        )}
    </GridItem>
  );
};
